import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { type Address } from 'viem';
import { createVerificationService } from '../services/VerificationService';
import { useAirdropStore } from '../stores/airdropStore';

const POLL_INTERVAL = 5000; // 5 seconds

/**
 * useUserCheck - Check user registration status with automatic polling
 *
 * Polls the user API every 5 seconds when the user is not registered,
 * automatically stops when the user is found.
 * On success, triggers checkPalmStatus to continue the verification flow.
 */
export function useUserCheck(address: Address | undefined) {
  const userExists = useAirdropStore((state) => state.userExists);
  const setUserStatus = useAirdropStore((state) => state.setUserStatus);
  const checkPalmStatus = useAirdropStore((state) => state.checkPalmStatus);

  const { data } = useQuery({
    queryKey: ['userCheck', address],
    queryFn: async () => {
      const service = createVerificationService();
      return service.checkUser(address!);
    },
    enabled: !!address,
    // Poll every 5s only when user is not found
    refetchInterval: userExists === false ? POLL_INTERVAL : false,
    // Pause polling when tab is hidden
    refetchIntervalInBackground: false,
    retry: 2,
  });

  // Sync query result to zustand store
  useEffect(() => {
    if (!data) return;

    if (data.exists && userExists !== true) {
      setUserStatus(true);
      void checkPalmStatus();
    } else if (!data.exists && userExists !== false) {
      setUserStatus(false);
    }
  }, [data, userExists, setUserStatus, checkPalmStatus]);
}
